/**
 * Central API Client for SkillMesh
 * Connects directly to the server-side FastAPI & Neo4j Database
 * Ensures all users share and synchronize real projects, people, skills, and graph links.
 */

const API_BASE = process.env.REACT_APP_API_URL || (
  typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000'
    : ''
);

// Helper to make API requests with automatic fallback to localStorage
async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  try {
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      ...options
    });
    if (!res.ok) {
      throw new Error(`API error ${res.status}: ${res.statusText}`);
    }
    return await res.json();
  } catch (err) {
    console.warn(`[SkillMesh API] Request failed for ${endpoint}:`, err.message);
    throw err;
  }
}

export const DEFAULT_PRELOADED_SKILLS = [
  { id: '1', name: 'React', category: 'Frontend', level: 'Advanced' },
  { id: '2', name: 'Python', category: 'Backend', level: 'Intermediate' },
  { id: '3', name: 'TypeScript', category: 'Frontend', level: 'Intermediate' },
  { id: '4', name: 'Node.js', category: 'Backend', level: 'Intermediate' },
  { id: '5', name: 'PostgreSQL', category: 'Database', level: 'Intermediate' },
  { id: '6', name: 'UI/UX Design', category: 'Design', level: 'Advanced' },
  { id: '7', name: 'Docker', category: 'Infrastructure', level: 'Intermediate' },
  { id: '8', name: 'Machine Learning', category: 'AI/ML', level: 'Beginner' },
  { id: '9', name: 'FastAPI', category: 'Backend', level: 'Intermediate' },
  { id: '10', name: 'AWS', category: 'Infrastructure', level: 'Intermediate' },
  { id: '11', name: 'DevOps', category: 'Infrastructure', level: 'Intermediate' },
  { id: '12', name: 'Product Strategy', category: 'Business', level: 'Advanced' },
  { id: '13', name: 'GraphQL', category: 'Backend', level: 'Intermediate' },
  { id: '14', name: 'Tailwind CSS', category: 'Frontend', level: 'Advanced' },
  { id: '15', name: 'Kubernetes', category: 'Infrastructure', level: 'Intermediate' },
  { id: '16', name: 'Figma', category: 'Design', level: 'Advanced' },
  { id: '17', name: 'Agile & Scrum', category: 'Business', level: 'Advanced' },
  { id: '18', name: 'Quality Assurance', category: 'QA/Testing', level: 'Intermediate' },
  { id: '19', name: 'Cybersecurity', category: 'Infrastructure', level: 'Intermediate' },
  { id: '20', name: 'Data Engineering', category: 'AI/ML', level: 'Intermediate' }
];

export const api = {
  // ==================== PROJECTS ====================
  async getProjects() {
    try {
      const data = await request('/api/projects');
      if (Array.isArray(data)) {
        localStorage.setItem('skillmesh_projects', JSON.stringify(data));
        return data;
      }
    } catch (e) {
      // Fallback to cached localStorage
    }
    return JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
  },

  async createProject(projectData) {
    try {
      const created = await request('/api/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      });
      const existing = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const updated = [created, ...existing.filter(p => p.id !== created.id)];
      localStorage.setItem('skillmesh_projects', JSON.stringify(updated));
      return created;
    } catch (e) {
      console.warn('Saving project locally due to API failure:', e);
      const existing = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const fallbackProject = {
        ...projectData,
        id: projectData.id || `proj_${Date.now()}`
      };
      localStorage.setItem('skillmesh_projects', JSON.stringify([fallbackProject, ...existing]));
      return fallbackProject;
    }
  },

  async updateProject(projectId, projectData) {
    try {
      const updated = await request(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: 'PUT',
        body: JSON.stringify(projectData)
      });
      const existing = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const updatedList = existing.map(p => (String(p.id) === String(projectId) || p.name === projectId) ? { ...p, ...updated } : p);
      localStorage.setItem('skillmesh_projects', JSON.stringify(updatedList));
      window.dispatchEvent(new Event('storage'));
      return updated;
    } catch (e) {
      console.warn('Updating project locally due to API failure:', e);
      const existing = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const updatedList = existing.map(p => (String(p.id) === String(projectId) || p.name === projectId) ? { ...p, ...projectData } : p);
      localStorage.setItem('skillmesh_projects', JSON.stringify(updatedList));
      window.dispatchEvent(new Event('storage'));
      return { ...projectData, id: projectId };
    }
  },

  async deleteProject(projectId) {
    try {
      await request(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('Backend delete project failed, updating local state:', e);
    }
    const existing = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
    const updated = existing.filter(p => String(p.id) !== String(projectId) && p.name !== projectId);
    localStorage.setItem('skillmesh_projects', JSON.stringify(updated));
    return true;
  },

  // ==================== PEOPLE ====================
  async getPeople() {
    let list = [];
    try {
      const data = await request('/api/people');
      if (Array.isArray(data)) {
        list = data;
      }
    } catch (e) {
      list = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    }
    // Deduplicate by normalized email and id
    const seen = new Set();
    const unique = [];
    list.forEach(p => {
      const em = (p.email || '').toLowerCase().trim();
      const key = em || String(p.id);
      if (key && !seen.has(key)) {
        seen.add(key);
        unique.push(p);
      }
    });
    localStorage.setItem('skillmesh_people', JSON.stringify(unique));
    return unique;
  },

  async createPerson(personData) {
    const personEmail = (personData.email || '').toLowerCase().trim();
    try {
      const created = await request('/api/people', {
        method: 'POST',
        body: JSON.stringify(personData)
      });
      const existing = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const filtered = existing.filter(p => {
        const em = (p.email || '').toLowerCase().trim();
        return String(p.id) !== String(created.id) && em !== personEmail;
      });
      const updated = [created, ...filtered];
      localStorage.setItem('skillmesh_people', JSON.stringify(updated));
      return created;
    } catch (e) {
      console.warn('Saving person locally due to API failure:', e);
      const existing = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const fallbackPerson = {
        ...personData,
        id: personData.id || `person_${Date.now()}`
      };
      const filtered = existing.filter(p => {
        const em = (p.email || '').toLowerCase().trim();
        return String(p.id) !== String(fallbackPerson.id) && em !== personEmail;
      });
      const updated = [fallbackPerson, ...filtered];
      localStorage.setItem('skillmesh_people', JSON.stringify(updated));
      return fallbackPerson;
    }
  },

  async deletePerson(personId) {
    try {
      await request(`/api/people/${encodeURIComponent(personId)}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('Backend delete person failed, updating local state:', e);
    }
    const existing = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    const updated = existing.filter(p => String(p.id) !== String(personId) && p.email !== personId && p.name !== personId);
    localStorage.setItem('skillmesh_people', JSON.stringify(updated));
    return true;
  },

  // ==================== SKILLS ====================
  async getSkills() {
    const deletedList = JSON.parse(localStorage.getItem('skillmesh_deleted_skills') || '[]');
    try {
      const data = await request('/api/skills');
      if (Array.isArray(data) && data.length > 0) {
        const filtered = data.filter(s => {
          const sId = String(s.id || '').toLowerCase();
          const sName = String(s.name || '').toLowerCase();
          return !deletedList.includes(sId) && !deletedList.includes(sName);
        });
        localStorage.setItem('skillmesh_skills', JSON.stringify(filtered));
        return filtered;
      }
    } catch (e) {
      // Fallback
    }
    const cached = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
    if (cached.length > 0) {
      return cached.filter(s => {
        const sId = String(s.id || '').toLowerCase();
        const sName = String(s.name || '').toLowerCase();
        return !deletedList.includes(sId) && !deletedList.includes(sName);
      });
    }
    return DEFAULT_PRELOADED_SKILLS.filter(s => !deletedList.includes(s.name.toLowerCase()));
  },

  async getAllUniqueSkills() {
    const deletedList = JSON.parse(localStorage.getItem('skillmesh_deleted_skills') || '[]');
    const isDeleted = (name, id) => {
      const n = String(name || '').toLowerCase().trim();
      const i = String(id || '').toLowerCase().trim();
      return (n && deletedList.includes(n)) || (i && deletedList.includes(i));
    };

    const fetched = await this.getSkills();
    const map = new Map();

    // 1. Add baseline preloaded skills (only if not deleted)
    DEFAULT_PRELOADED_SKILLS.forEach(s => {
      if (!isDeleted(s.name, s.id)) {
        map.set(s.name.toLowerCase(), { ...s });
      }
    });

    // 2. Add fetched skills from database / storage
    (fetched || []).forEach(s => {
      const name = typeof s === 'string' ? s : s?.name;
      if (name && !isDeleted(name, s.id)) {
        const key = name.toLowerCase();
        const existing = map.get(key) || {};
        map.set(key, {
          id: s.id || existing.id,
          name: name.trim(),
          category: s.category || existing.category || 'General',
          level: s.level || existing.level || 'Intermediate'
        });
      }
    });

    // 3. Add skills referenced in projects
    try {
      const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      projects.forEach(p => {
        (p.requiredSkills || []).forEach(sk => {
          const name = typeof sk === 'string' ? sk : sk?.name;
          if (name && !isDeleted(name)) {
            const key = name.toLowerCase();
            if (!map.has(key)) {
              map.set(key, {
                name: name.trim(),
                category: typeof sk === 'object' && sk.category ? sk.category : 'General',
                level: typeof sk === 'object' && sk.level ? sk.level : 'Intermediate'
              });
            }
          }
        });
      });
    } catch (e) {}

    // 4. Add skills referenced in people
    try {
      const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      people.forEach(p => {
        (p.skills || []).forEach(sk => {
          const name = typeof sk === 'string' ? sk : sk?.name;
          if (name && !isDeleted(name)) {
            const key = name.toLowerCase();
            if (!map.has(key)) {
              map.set(key, {
                name: name.trim(),
                category: typeof sk === 'object' && sk.category ? sk.category : 'General',
                level: typeof sk === 'object' && sk.level ? sk.level : 'Intermediate'
              });
            }
          }
        });
      });
    } catch (e) {}

    const list = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    return list;
  },

  async createSkill(skillData) {
    try {
      const created = await request('/api/skills', {
        method: 'POST',
        body: JSON.stringify(skillData)
      });
      // Remove from deleted list if re-created
      const deletedList = JSON.parse(localStorage.getItem('skillmesh_deleted_skills') || '[]');
      const nameKey = (created.name || '').toLowerCase().trim();
      const idKey = String(created.id || '').toLowerCase().trim();
      const updatedDeleted = deletedList.filter(d => d !== nameKey && d !== idKey);
      localStorage.setItem('skillmesh_deleted_skills', JSON.stringify(updatedDeleted));

      const existing = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
      const updated = [created, ...existing.filter(s => s.name.toLowerCase() !== created.name.toLowerCase())];
      localStorage.setItem('skillmesh_skills', JSON.stringify(updated));
      return created;
    } catch (e) {
      console.warn('Saving skill locally due to API failure:', e);
      const existing = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
      const fallbackSkill = {
        ...skillData,
        id: skillData.id || `skill_${Date.now()}`
      };
      localStorage.setItem('skillmesh_skills', JSON.stringify([fallbackSkill, ...existing]));
      return fallbackSkill;
    }
  },

  async deleteSkill(skillId, skillName) {
    let idStr = String(skillId || '').trim();
    let nameStr = String(skillName || '').trim();

    // 0. Auto-resolve name or ID if missing or ambiguous
    const cachedSkills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
    const match = cachedSkills.find(s => 
      (idStr && (String(s.id).toLowerCase() === idStr.toLowerCase() || String(s.name || '').toLowerCase() === idStr.toLowerCase())) ||
      (nameStr && (String(s.name || '').toLowerCase() === nameStr.toLowerCase() || String(s.id).toLowerCase() === nameStr.toLowerCase()))
    ) || DEFAULT_PRELOADED_SKILLS.find(s => 
      (idStr && (String(s.id).toLowerCase() === idStr.toLowerCase() || s.name.toLowerCase() === idStr.toLowerCase())) ||
      (nameStr && (s.name.toLowerCase() === nameStr.toLowerCase() || String(s.id).toLowerCase() === nameStr.toLowerCase()))
    );

    if (match) {
      if (!nameStr || nameStr === idStr) {
        nameStr = match.name || '';
      }
      if (!idStr || idStr === nameStr) {
        idStr = String(match.id || idStr);
      }
    }

    const nameToDelete = nameStr || idStr;
    const idToDelete = idStr || nameToDelete;

    // 1. Delete on backend by ID
    if (idToDelete) {
      try {
        await request(`/api/skills/${encodeURIComponent(idToDelete)}`, { method: 'DELETE' });
      } catch (e) {
        console.warn('Backend delete skill by ID failed:', e);
      }
    }

    // 2. Also delete on backend by Name if different from ID
    if (nameToDelete && nameToDelete.toLowerCase() !== idToDelete.toLowerCase()) {
      try {
        await request(`/api/skills/${encodeURIComponent(nameToDelete)}`, { method: 'DELETE' });
      } catch (e) {
        console.warn('Backend delete skill by Name failed:', e);
      }
    }

    // 3. Track in deleted skills storage so it never re-seeds
    const deletedList = JSON.parse(localStorage.getItem('skillmesh_deleted_skills') || '[]');
    const toBlacklist = [
      idToDelete.toLowerCase(),
      nameToDelete.toLowerCase(),
      `skill_${nameToDelete.toLowerCase().replace(/\s+/g, '_')}`,
      `skill_${idToDelete.toLowerCase().replace(/\s+/g, '_')}`
    ].filter(Boolean);

    toBlacklist.forEach(item => {
      if (!deletedList.includes(item)) {
        deletedList.push(item);
      }
    });
    localStorage.setItem('skillmesh_deleted_skills', JSON.stringify(deletedList));

    // 4. Update localStorage skills array
    const existing = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
    const updated = existing.filter(s => {
      const sId = String(s.id || '').toLowerCase();
      const sName = String(s.name || '').toLowerCase();
      const matchId = idToDelete && (sId === idToDelete.toLowerCase() || sName === idToDelete.toLowerCase());
      const matchName = nameToDelete && (sName === nameToDelete.toLowerCase() || sId === nameToDelete.toLowerCase());
      return !matchId && !matchName;
    });
    localStorage.setItem('skillmesh_skills', JSON.stringify(updated));

    // 5. Clean up removed skill from projects & people so it is fully purged
    try {
      const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      let projectsChanged = false;
      projects.forEach(p => {
        if (Array.isArray(p.requiredSkills)) {
          const prevLen = p.requiredSkills.length;
          p.requiredSkills = p.requiredSkills.filter(sk => {
            const skName = typeof sk === 'string' ? sk : sk?.name;
            const skLower = (skName || '').toLowerCase();
            return skLower !== nameToDelete.toLowerCase() && skLower !== idToDelete.toLowerCase();
          });
          if (p.requiredSkills.length !== prevLen) projectsChanged = true;
        }
      });
      if (projectsChanged) localStorage.setItem('skillmesh_projects', JSON.stringify(projects));

      const people = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      let peopleChanged = false;
      people.forEach(p => {
        if (Array.isArray(p.skills)) {
          const prevLen = p.skills.length;
          p.skills = p.skills.filter(sk => {
            const skName = typeof sk === 'string' ? sk : sk?.name;
            const skLower = (skName || '').toLowerCase();
            return skLower !== nameToDelete.toLowerCase() && skLower !== idToDelete.toLowerCase();
          });
          if (p.skills.length !== prevLen) peopleChanged = true;
        }
      });
      if (peopleChanged) localStorage.setItem('skillmesh_people', JSON.stringify(people));
    } catch (e) {}

    // 6. Notify other open components via storage event
    try {
      window.dispatchEvent(new Event('storage'));
    } catch (e) {}

    return true;
  },

  // ==================== ADMIN DATABASE EXPLORER ====================
  async getDatabaseStats() {
    try {
      return await request('/api/admin/database');
    } catch (e) {
      console.warn('Failed to fetch admin database stats:', e);
      return null;
    }
  },

  async runCypher(query) {
    try {
      return await request('/api/admin/cypher', {
        method: 'POST',
        body: JSON.stringify({ query })
      });
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // ==================== GRAPH & CLUSTERS ====================
  async getGraphData() {
    try {
      return await request('/api/graph/data');
    } catch (e) {
      return null;
    }
  },

  async getGraphClusters() {
    try {
      return await request('/api/graph/clusters');
    } catch (e) {
      return null;
    }
  },

  // ==================== MATCHING ====================
  async getMatches(projectId = null) {
    try {
      const q = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
      return await request(`/api/matching${q}`);
    } catch (e) {
      return [];
    }
  },

  // ==================== APPLICATIONS ====================
  async getApplications(email = null) {
    try {
      const q = email ? `?email=${encodeURIComponent(email)}` : '';
      const data = await request(`/api/applications${q}`);
      if (Array.isArray(data)) {
        if (!email) {
          localStorage.setItem('skillmesh_applications', JSON.stringify(data));
        }
        return data;
      }
    } catch (e) {
      // Fallback
    }
    const all = JSON.parse(localStorage.getItem('skillmesh_applications') || '[]');
    if (email) {
      return all.filter(a => (a.userEmail || '').toLowerCase() === email.toLowerCase());
    }
    return all;
  },

  async getProjectApplications(projectId) {
    try {
      const data = await request(`/api/projects/${encodeURIComponent(projectId)}/applications`);
      if (Array.isArray(data)) {
        return data;
      }
    } catch (e) {
      // Fallback
    }
    const all = JSON.parse(localStorage.getItem('skillmesh_applications') || '[]');
    return all.filter(a => String(a.projectId) === String(projectId) || a.projectName === projectId);
  },

  async applyToProject(applicationData) {
    const projId = applicationData.projectId || applicationData.project_id;
    try {
      const res = await request(`/api/projects/${encodeURIComponent(projId)}/apply`, {
        method: 'POST',
        body: JSON.stringify(applicationData)
      });
      const existing = JSON.parse(localStorage.getItem('skillmesh_applications') || '[]');
      const filtered = existing.filter(a => !(String(a.projectId) === String(projId) && (a.userEmail || '').toLowerCase() === (applicationData.userEmail || '').toLowerCase()));
      const updated = [res, ...filtered];
      localStorage.setItem('skillmesh_applications', JSON.stringify(updated));
      window.dispatchEvent(new Event('storage'));
      return res;
    } catch (e) {
      console.warn('Saving application locally due to API failure:', e);
      const appRecord = {
        id: `app_${Date.now()}`,
        projectId: String(projId),
        projectName: applicationData.projectName || 'Project',
        userEmail: (applicationData.userEmail || '').toLowerCase(),
        userName: applicationData.userName || 'Applicant',
        message: applicationData.message || '',
        skills: applicationData.skills || [],
        appliedAt: new Date().toISOString(),
        status: 'Pending'
      };
      const existing = JSON.parse(localStorage.getItem('skillmesh_applications') || '[]');
      const filtered = existing.filter(a => !(String(a.projectId) === String(projId) && (a.userEmail || '').toLowerCase() === (applicationData.userEmail || '').toLowerCase()));
      const updated = [appRecord, ...filtered];
      localStorage.setItem('skillmesh_applications', JSON.stringify(updated));
      window.dispatchEvent(new Event('storage'));
      return appRecord;
    }
  },

  async updateApplicationStatus(projectId, applicantEmail, status) {
    try {
      await request(`/api/projects/${encodeURIComponent(projectId)}/applications/${encodeURIComponent(applicantEmail)}/status`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
    } catch (e) {
      console.warn('API error updating application status, applying locally:', e);
    }

    const existing = JSON.parse(localStorage.getItem('skillmesh_applications') || '[]');
    const emailClean = (applicantEmail || '').toLowerCase();
    const updated = existing.map(a => {
      if ((String(a.projectId) === String(projectId) || a.projectName === projectId) && (a.userEmail || '').toLowerCase() === emailClean) {
        return { ...a, status };
      }
      return a;
    });
    localStorage.setItem('skillmesh_applications', JSON.stringify(updated));

    if (status === 'Accepted') {
      const projects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const updatedProjects = projects.map(p => {
        if (String(p.id) === String(projectId) || p.name === projectId) {
          const members = Array.isArray(p.teamMembers) ? [...p.teamMembers] : [];
          const app = existing.find(a => (String(a.projectId) === String(projectId) || a.projectName === projectId) && (a.userEmail || '').toLowerCase() === emailClean);
          const memberName = app?.userName || emailClean.split('@')[0];
          if (!members.some(m => (typeof m === 'string' ? m : m.name) === memberName)) {
            members.push(memberName);
          }
          return {
            ...p,
            teamMembers: members,
            currentTeam: members.length
          };
        }
        return p;
      });
      localStorage.setItem('skillmesh_projects', JSON.stringify(updatedProjects));
    }

    window.dispatchEvent(new Event('storage'));
    return { success: true, status };
  },

  // Initial synchronization on app start
  async syncAll() {
    try {
      const savedUser = JSON.parse(localStorage.getItem('skillmesh_user') || 'null');
      if (savedUser && savedUser.email && Array.isArray(savedUser.skills) && savedUser.skills.length > 0) {
        const existingPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
        const uEmail = savedUser.email.toLowerCase().trim();
        const exists = existingPeople.some(p => (p.email || '').toLowerCase().trim() === uEmail);
        if (!exists) {
          try {
            await this.createPerson({
              id: savedUser.id || uEmail,
              name: savedUser.name || (savedUser.email ? savedUser.email.split('@')[0] : 'Member'),
              role: savedUser.title || 'SkillMesh Member',
              team: savedUser.skills[0]?.category || 'General',
              email: uEmail,
              availability: savedUser.availability || 'Available',
              skills: savedUser.skills.map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean),
              experience: `${savedUser.experienceYears !== undefined && savedUser.experienceYears !== null ? savedUser.experienceYears : 0} years`
            });
          } catch (e) {
            console.warn('Auto-sync user profile to Neo4j error:', e);
          }
        }
      }

      await Promise.allSettled([
        this.getProjects(),
        this.getPeople(),
        this.getSkills(),
        this.getApplications()
      ]);
    } catch (e) {
      console.warn('Initial sync error:', e);
    }
  }
};

export default api;
